import { useState } from 'react';
import { useFormik } from 'formik';
import { toFormikValidate, toFormikValidationSchema } from 'zod-formik-adapter';
import { ExternalLink } from 'lucide-react';
import { workspace_v1_AdminConfig } from '@antimatterhq/asl-api-ts-v1';
import toast from 'react-hot-toast';
import { Button } from '@antimatterhq/ui/components/button';
import { Input } from '@antimatterhq/ui/components/input';
import { Label } from '@antimatterhq/ui/components/label';
import { Text } from '@antimatterhq/ui/components/typography';
import {
  ConfigAdminSchema,
  ConfigAdminFormValues,
} from '@/zod/schemas/ConfigAdmin';
import { useGetAdminConfig } from '@/hooks/workspace/useGetAdminConfig';
import { useUpdateAdminConfig } from '@/hooks/workspace/useUpdateAdminConfig';
import { useVerifyAuth } from '@/hooks/dbui/useVerifyAuth';

const AdminPage = () => {
  const [isChangingCredentials, setIsChangingCredentials] = useState(false);

  const { data } = useGetAdminConfig();

  const { mutateAsync: updateAdminConfig, isPending: isUpdating } =
    useUpdateAdminConfig();
  // TODO: URL TBD
  const workspaceUrl =
    data?.spec?.auth?.host || 'https://accounts.gcp.databricks.com';

  const initialValues: ConfigAdminFormValues = {
    clientId: data?.spec?.auth?.servicePrincipal?.clientId || '',
    clientSecret: '',
  };

  const { values, errors, touched, handleSubmit, handleChange, resetForm } =
    useFormik({
      initialValues,
      validationSchema: toFormikValidationSchema(ConfigAdminSchema),
      validate: toFormikValidate(ConfigAdminSchema),
      onSubmit: async (newValues) => {
        try {
          const adminConfig: workspace_v1_AdminConfig = {
            spec: {
              auth: {
                host: workspaceUrl,
                servicePrincipal: {
                  clientId: newValues.clientId,
                  secret: newValues.clientSecret,
                },
              },
            },
          };
          await updateAdminConfig(adminConfig);

          toast.success('Credentials updated successfully');

          resetForm({ values: initialValues });
          setIsChangingCredentials(false);
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to update credentials';
          toast.custom((t: { visible: boolean }) => (
            <div
              className={`${
                t.visible ? 'animate-enter' : 'animate-leave'
              } pointer-events-auto flex w-full max-w-md rounded-lg bg-red-500 shadow-lg ring-1 ring-black ring-opacity-5`}
            >
              <div className="flex items-center gap-4 p-4">
                <div className="flex flex-col">
                  <Text className="block" bold>
                    Changed Client Credentials Failed
                  </Text>
                  <p className="mt-2 text-sm">{errorMessage}</p>
                </div>

                <Button
                  type="button"
                  variant="default"
                  className="text-sm"
                  onClick={() => {
                    setIsChangingCredentials(false);
                    resetForm({ values: initialValues });
                  }}
                >
                  Try again
                </Button>
              </div>
            </div>
          ));
        }
      },
    });

  return (
    <div className="w-full">
      <Text size="2xl" className="mb-2 w-full text-xl font-bold">
        Admin config
      </Text>
      <Text className="mb-2 block" size="xl">
        Databricks workspace URL:
      </Text>
      <a
        href={workspaceUrl}
        className="flex items-center text-blue-500"
        target="_blank"
        rel="noreferrer"
      >
        <Text className="mr-1 block underline">{workspaceUrl}</Text>{' '}
        <ExternalLink size={18} />
      </a>
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="pt-8">
            <Text className="mb-2 block" size="lg">
              Service principal client credentials
            </Text>
            <div className="space-y-4">
              <div>
                <Label htmlFor="clientId" id="clientId-label">
                  Client ID
                </Label>
                <div className="mt-1.5 flex items-start gap-4">
                  <Input
                    id="clientId"
                    name="clientId"
                    disabled={!isChangingCredentials}
                    className="max-w-[430px] font-mono"
                    value={values.clientId}
                    onChange={handleChange}
                    placeholder={initialValues.clientId}
                    aria-labelledby="clientId-label"
                  />
                  <Button
                    type="button"
                    variant="default"
                    className="h-10"
                    onClick={() =>
                      setIsChangingCredentials(!isChangingCredentials)
                    }
                    disabled={isChangingCredentials}
                  >
                    Change Client Credentials
                  </Button>
                </div>
              </div>

              {isChangingCredentials && (
                <div>
                  <Label htmlFor="clientSecret" id="clientSecret-label">
                    New service principal client secret
                  </Label>
                  <Input
                    id="clientSecret"
                    name="clientSecret"
                    placeholder="Service principal client secret"
                    className="mt-1.5 max-w-[430px]"
                    value={values.clientSecret}
                    onChange={handleChange}
                    aria-labelledby="clientSecret-label"
                  />
                  {errors.clientSecret && touched.clientSecret && (
                    <div className="mt-1 text-sm text-red-500">
                      {errors.clientSecret}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {isChangingCredentials && (
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="default"
              onClick={() => {
                setIsChangingCredentials(false);
                resetForm({ values: initialValues });
              }}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Confirm new credentials'}
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default function Admin() {
  const { data } = useVerifyAuth();

  if (!data?.isAdmin) {
    return (
      <div>
        <Text color="error" size="lg">
          You do not have permission to access admin config
        </Text>
      </div>
    );
  }

  return <AdminPage />;
}
